import React, { useState, useEffect, cloneElement } from 'react'
import cn from 'classnames'
import s from './CollapsibleContainer.module.scss'
import type { Maybe } from '@pylot-data/pylotschema'
import { Minus, Plus } from 'react-feather'
import { isMobileScreen } from '@lib/utils/mediaQuery'
import Button from '../../Button'

export type CollapsibleContainerProps = {
    children: JSX.Element
    title?: JSX.Element | Maybe<string>
    isOpen?: boolean
    iconOpen?: JSX.Element | null | false
    iconClose?: JSX.Element | null | false
    onOpen?: () => void
    onClose?: () => void
    classes?: string
    callback?: (title: JSX.Element | Maybe<string>) => void
}

export const CollapsibleContainer = (
    props: CollapsibleContainerProps
): JSX.Element => {
    const {
        children,
        isOpen = false,
        title,
        iconOpen = <Minus width="20" height="20" />,
        iconClose = <Plus width="20" height="20" />,
        classes = ''
    } = props

    const [opened, setOpened] = useState(isOpen)

    const isMobile = isMobileScreen()

    useEffect(() => {
        if (opened && props.onOpen) {
            props.onOpen()
        } else if (!opened && props.onClose) {
            props.onClose()
        }
    }, [opened, props])

    const toggle = () => {
        setOpened(!opened)

        if (!opened && props.callback && typeof props.callback === 'function') {
            props.callback(title || '')
        }
    }

    return (
        <div className={cn(s['collapsible-container'], classes)}>
            {isMobile ? (
                <div className={cn(s['collapsible'], isOpen && 'opened')}>
                    <div className={cn(s['header'], 'collapsible-header')}>
                        <Button
                            variant="link"
                            className={cn(s['toggle'], 'toggle-btn')}
                            type="button"
                            onClick={toggle}
                            aria-expanded={isOpen ? 'false' : 'true'}
                        >
                            {isOpen ? iconOpen : iconClose}
                            <h2
                                className={cn(
                                    s['title'],
                                    'collapsible-header-title'
                                )}
                            >
                                {title}
                            </h2>
                        </Button>
                    </div>
                    <div className="inner">
                        <div
                            className="collapsible-content"
                            aria-hidden={isOpen ? 'false' : 'true'}
                        >
                            {cloneElement(children, { setOpened })}
                        </div>
                    </div>
                </div>
            ) : (
                <div className={cn(s['collapsible'], opened && 'opened')}>
                    <div className={cn(s['header'], 'collapsible-header')}>
                        <Button
                            variant="link"
                            className={cn(s['toggle'], 'toggle-btn')}
                            type="button"
                            onClick={toggle}
                            aria-expanded={opened ? 'false' : 'true'}
                        >
                            {isOpen ? iconOpen : iconClose}
                            <h2
                                className={cn(
                                    s['title'],
                                    'collapsible-header-title'
                                )}
                            >
                                {title}
                            </h2>
                        </Button>
                    </div>
                    <div className="inner">
                        <div
                            className="collapsible-content"
                            aria-hidden={opened ? 'false' : 'true'}
                        >
                            {cloneElement(children, { setOpened })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default CollapsibleContainer
